import AppLoadingAnimation from "@/_components/AppLoadingAnimation"
import ButtonWithTooltip from "@/_components/ButtonWithTooltip"
import CustomButton from "@/_components/CustomButton"
import DebugPopover from "@/_components/DebugPopover"
import EntityModelModal from "@/_components/EntityModelModal"
import LayoutCard, { cardNames } from "@/_components/LayoutCard"
import LazySkeletonUi from "@/_components/LazySkeletonUi"
import PanelOuter from "@/_components/PanelOuter"
import { loc } from "@/_services/localization"
import { isOffline } from "@/_services/offlineService"
import { getUiState, setUiState } from "@/_services/uiState"
import { getConfigAtPath, getOptions } from "@/_services/userConfiguration"
import {
  applyClasses,
  cardsCollapseToggleId,
  customEvents,
  debug,
  domAttributes,
  downloadFileFromUrl,
  execKatex,
  handleAccessibleOnKeyDown,
  localStorageKeys,
  navCss,
  sideColumnToggleId,
  softReloadRoute,
} from "@/_services/utils"
import { getFieldProps, labelToName } from "basikon-common-utils"
import React, { Suspense } from "react"
import { Col, Modal, Row } from "react-bootstrap"
import { Link, withRouter } from "react-router-dom"

class Card extends React.Component {
  constructor(props) {
    super(props)

    const { collapse, setRef } = props
    this.state = {
      expanded: collapse !== undefined ? !collapse : true,
      toggleExpanded: true,
      areCardsToggled: false,
    }

    this.cardRef = React.createRef()
    if (typeof setRef === "function") setRef(this.cardRef)
  }

  componentDidMount() {
    this.cardRef.current.addEventListener(customEvents.card.scrollToModelField, this.scrollToModelField, true)
    window.addEventListener(customEvents.card.openTabCallback, this.scrollToModelField, true)
    window.addEventListener(customEvents.card.toggle, this.eventToggle, true)
  }

  componentWillUnmount() {
    this.cardRef.current.removeEventListener(customEvents.card.scrollToModelField, this.scrollToModelField, true)
    window.removeEventListener(customEvents.card.openTabCallback, this.scrollToModelField, true)
    window.removeEventListener(customEvents.card.toggle, this.eventToggle, true)
  }

  componentDidUpdate(prevProps) {
    const { collapse } = this.props
    if (prevProps.collapse !== collapse) {
      this.setExpanded(collapse !== undefined ? !collapse : true)
    }
  }

  // Notify onSetExpanded only on actual changes so that cards controlling their collapse prop from it don't loop
  setExpanded = (expanded, otherState) => {
    const { onSetExpanded } = this.props
    const hasChanged = expanded !== this.state.expanded
    this.setState({ expanded, ...otherState })
    if (hasChanged && onSetExpanded) onSetExpanded(expanded)
  }

  eventToggle = () => {
    const { toggleExpanded } = this.state
    const { collapse } = this.props
    const isCollapsible = collapse !== undefined && collapse !== null
    if (isCollapsible) {
      this.setExpanded(!toggleExpanded, { toggleExpanded: !toggleExpanded })
    }
  }

  focusOnElement = (modelFieldElement, options) => {
    const formGroupCssErrorClass = options?.errorClass || "form-group-error-focus"
    modelFieldElement.scrollIntoView({ behavior: "smooth", block: "center" })
    modelFieldElement.classList.add(formGroupCssErrorClass)
    setTimeout(() => modelFieldElement.classList.remove(formGroupCssErrorClass), 2000)
  }

  getModelFieldPathAttribute = modelFieldPath => `[${domAttributes.modelFieldPath}="${modelFieldPath}"]`

  getDocumentModelFieldPathElement = modelFieldPath => {
    return document.querySelector(this.getModelFieldPathAttribute(modelFieldPath))
  }

  getInnerPanelModelFieldPathElement = modelFieldPath => {
    return this.cardRef.current?.querySelector(`.panel-inner ${this.getModelFieldPathAttribute(modelFieldPath)}`)
  }

  getModelFieldPathElement = modelFieldPath => {
    return this.cardRef.current?.querySelector(this.getModelFieldPathAttribute(modelFieldPath))
  }

  /**
   * If the modelFieldElement is found in the child DOM of this card (static card content) then expand the card if needed and focus on the element
   * Otherwise :
   * - If the modelFieldElement is found somewhere in the DOM, then it is handled by another Card component instance, in this instance we don't anything to do
   * - If this card has the props onSetExpanded (dynamic card content, loaded when the card is expanded), expand it
   * - If the card has the props openDetails (dynamic card content details), open the details
   * - Then try to find the modelFieldElement again and if found, focus on it
   * Otherwise do nothing
   */
  scrollToModelField = async event => {
    const { onSetExpanded, openDetails } = this.props
    const { type, detail } = event
    const { modelFieldPath } = detail || {}
    let options
    const existingInnerPanelModelFieldElement = this.getInnerPanelModelFieldPathElement(modelFieldPath)
    let existingModelFieldElement = existingInnerPanelModelFieldElement || this.getModelFieldPathElement(modelFieldPath)
    if (!existingModelFieldElement && this.cardRef.current.getAttribute(domAttributes.modelFieldPath) === modelFieldPath) {
      existingModelFieldElement = this.cardRef.current
      options = { errorClass: "card-error-focus" }
    }

    if (existingInnerPanelModelFieldElement) {
      existingInnerPanelModelFieldElement.dispatchEvent(new CustomEvent(customEvents.innerPanel.open))
    }

    if (existingModelFieldElement) {
      if (openDetails) openDetails()
      setTimeout(() => {
        if (this.state.expanded) {
          this.focusOnElement(existingModelFieldElement, options)
        } else {
          this.toggle(true)
          setTimeout(() => this.focusOnElement(existingModelFieldElement, options), 370) // 370 is roughly the duration of the bootstrap height transition
        }
      })
      return
    }

    // This one is special because the persons are often presented in tabs.
    // Only one tab is rendered at a time (we could let them all render but this is costly)
    // so we must open and render the proper one before scroll to a field inside it.
    if (type === customEvents.card.scrollToModelField && modelFieldPath.startsWith("persons")) {
      window.dispatchEvent(new CustomEvent(customEvents.personComponent.openTab, { detail: { modelFieldPath } }))
      return
    }

    if (this.getDocumentModelFieldPathElement(modelFieldPath)) return

    if (onSetExpanded) this.toggle(true)
    if (openDetails) openDetails()

    // timeout to let react render the details
    setTimeout(() => {
      const probablyExistingInnerPanelModelFieldElement = this.getInnerPanelModelFieldPathElement(modelFieldPath)
      const probablyExistingModelFieldElement = this.getModelFieldPathElement(modelFieldPath)
      if (probablyExistingInnerPanelModelFieldElement) {
        probablyExistingInnerPanelModelFieldElement.dispatchEvent(new CustomEvent(customEvents.innerPanel.open))
      }
      if (probablyExistingModelFieldElement) {
        this.focusOnElement(probablyExistingModelFieldElement)
      }
    })
  }

  toggle = forceExpand => {
    const { onSetExpanded } = this.props
    const expanded = forceExpand || !this.state.expanded
    this.setState({ expanded })
    onSetExpanded && onSetExpanded(expanded)
  }

  /**
   * Warning: this is a diminished copy for FormContent handleButtonClick and should mirror it for sake of consistency.
   * The main difference is how options are passed.
   * Here they are passed in the action object.
   */
  handleActionButtonClick = async ({ url, linkTo, download, softReload, payload } = {}) => {
    const { history } = this.props

    if (linkTo) {
      if (typeof linkTo === "string") {
        if (linkTo.startsWith("http")) {
          window.location.href = linkTo
        } else {
          history.push(linkTo)
        }
      } else if (typeof linkTo === "object" && linkTo.url) {
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/open
        window.open(linkTo.url, linkTo.target, linkTo.windowFeatures)
      }
      return
    }

    if (url && download) return await downloadFileFromUrl(url, "Downloaded", "Downloading", payload)

    if (softReload) {
      softReloadRoute()
    }
  }

  render() {
    const {
      debugInfo,
      legend,
      stats,
      title,
      titleColProps,
      titleSideText,
      titleLinkTo,
      titleIcon,
      category,
      children,
      ftTextCenter,
      hidden,
      titleClassName,
      content,
      isHtmlContent,
      isMarkdownContent,
      onHtmlClick,
      collapse,
      action,
      className = "",
      disabled,
      noCard,
      dataAttributes,
      linkTo,
      download,
      modelFieldPath,
      entity,
      isEntityWorkflowCard,
    } = this.props

    let _modelFieldPath = modelFieldPath || debugInfo?.props?.modelFieldPath
    if (!_modelFieldPath && title) _modelFieldPath = labelToName(title)
    if (_modelFieldPath && debugInfo?.props && !debugInfo.props.modelFieldPath) debugInfo.props.modelFieldPath = _modelFieldPath
    const cardProps = entity ? getFieldProps(entity, _modelFieldPath) : {}

    const { expanded, showModalPopover } = this.state
    const { collapsible = 11, expandedWithAction = 6 } = titleColProps?.sm || {}

    /**
     * calculate isCollapsible here instead of constructor an keep in state.
     * A bug is detected when a script used in a page by using importscript, some cards contrucctor of this lib are not called
     */
    const isCollapsible = collapse !== undefined && collapse !== null
    const _dataAttributes = {}
    Object.keys(dataAttributes || {}).forEach(key => {
      if (key.startsWith("data-")) _dataAttributes[key] = dataAttributes[key]
    })

    const body =
      isHtmlContent || isMarkdownContent ? (
        <div
          className={"content" + (isMarkdownContent ? " md-content" : "")}
          onClick={onHtmlClick}
          // Post-render KaTeX pass for markdown cards; lazily loads the katex chunk only when
          // math is present. Central hook so every markdown card renders LaTeX (see execKatex).
          ref={isMarkdownContent ? el => execKatex({ element: el, html: content }) : undefined}
          dangerouslySetInnerHTML={{
            // Markdown is sanitized in ContentComponent.
            // Sanitizing html content can lead to removed customized html attributes (e.g onClickHandler)
            __html: content,
          }}
        />
      ) : action ? (
        // We need to use a row here to keep the z-index order, notably when there are dropdowns in the card header.
        // However custom pages might have CSS not compatible with that wrapping, so we limit it to card having a header with actions.
        <Row className="content">
          <Col xs={12}>
            {content}
            {children}
          </Col>
        </Row>
      ) : (
        <div className="content">
          {content}
          {children}
        </div>
      )

    const cardClasses = applyClasses({
      card: true,
      [className]: true,
      "card-hidden": hidden,
      "card-html": isHtmlContent,
      "no-card": noCard,
      disabled,
    })

    const _headerClasses = {
      header: true,
      [titleClassName]: true,
      "c-pointer": isCollapsible,
    }
    // 5px is the same as the divider vertical margins (divider between top and bottom transitions)
    if (isEntityWorkflowCard) _headerClasses["pdb-5px"] = true

    const headerClasses = applyClasses(_headerClasses)
    const isActionButtonConfig = action && typeof action === "object" && (action.label || action.onClick || action.bsStyle)

    const customAction = isActionButtonConfig && (
      <CustomButton
        bsStyle={action.bsStyle || "default"}
        bsSize={action.bsSize || "sm"}
        fill={action.fill}
        className={(action.pullRight ? "pull-right " : "") + action.className || ""}
        onClick={typeof action.onClick === "function" ? action.onClick : () => this.handleActionButtonClick(action)}
        disabled={action.disabled}
      >
        {action.label ? loc(action.label) : action.children}
      </CustomButton>
    )

    let titleColSize = 12
    if (isCollapsible) titleColSize = collapsible
    if (expanded && action) titleColSize = expandedWithAction
    const titleCol = (
      // we need a min-height to avoid flicker when clicking on selectable rows in entities page
      <Col xs={isCollapsible ? 11 : 12} sm={titleColSize} className="title-col flex align-items-center">
        <h4 className="title grow">
          {/* WARNING: when title is a React component, we don't want to use inline-flex to not break the style of the componet. Example for the LoginPage.jsx. */}
          <span
            tabIndex={isCollapsible ? "0" : "-1"}
            className={`${titleIcon && typeof title === "string" ? "inline-flex align-items-center" : ""}${isCollapsible ? " outline-accessible" : ""}`}
            onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: () => isCollapsible && this.toggle() })}
          >
            {titleIcon && <i className={`mr-5px ${titleIcon}`} />}
            {title}
          </span>

          {titleSideText?.type === "badge" ? (
            <CustomButton bsStyle={titleSideText?.style || "info"} bsSize="xs" className="ml-5px" fill round>
              {titleSideText.label}
            </CustomButton>
          ) : titleSideText ? (
            <span className="white-space-nowrap ml-5px mr-5px">{titleSideText}</span>
          ) : (
            <></>
          )}

          {titleLinkTo && (
            <a
              href={window.location.origin + titleLinkTo}
              aria-label={`${loc("Link to")} ${window.location.origin + titleLinkTo}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex"
              onClick={event => event.stopPropagation()}
            >
              <i className={`icn-external-link icn-xs text-link ${titleSideText ? "" : "ml-5px"}`} />
            </a>
          )}
        </h4>
      </Col>
    )

    const actionCol = (action || isCollapsible) && (
      <>
        {isCollapsible && (
          <Col xs={1} className={"h-30" + (action ? " card-collapse-col" : "")}>
            <div className={"float-right flex-center justify-content-end h-100" + (action ? " ml-5px" : "")}>
              <i className="section-collapse-icon icn-chevron-right icn-xs pull-right" {...{ [domAttributes.isExpanded]: expanded }} />
            </div>
          </Col>
        )}

        {action && (
          <Col
            xs={12}
            sm={12 - titleColSize}
            // The collapse icon on the right, then the actions: in a narrow card, the actions wrap below the icon and the header
            // grows to contain them, instead of overflowing on the content that follows (e.g. the workflow buttons of the entities)
            className="h-30 flex flex-dir-row-reverse flex-wrap align-items-center"
          >
            {isCollapsible && (
              <div className={"card-collapse-action flex-center justify-content-end" + (action ? " ml-5px" : "")}>
                <i className="section-collapse-icon icn-chevron-right icn-xs pull-right" {...{ [domAttributes.isExpanded]: expanded }} />
              </div>
            )}

            {expanded && (
              // The actions are often floating buttons: their container takes their height
              <div className="d-flow-root" onClick={e => e.stopPropagation()}>
                {isActionButtonConfig ? (
                  <span className="ml-5px" onClick={e => e.stopPropagation()}>
                    {customAction}
                  </span>
                ) : (
                  action
                )}
              </div>
            )}
          </Col>
        )}
      </>
    )

    const popover = debug && debugInfo?.card && (
      <DebugPopover help={'Card "' + debugInfo.card + '"'} debug={debug} onClick={() => this.setState({ showModalPopover: true })} />
    )

    const message = cardProps?.errorFormat || cardProps?.warningFormat
    const displayHeaderRow = !!(title || category || actionCol)
    const card = (
      // warning : exceptionnally data-is-expanded (domAttributes.isExpanded) is also used as an e2e test handle
      <div
        className={cardClasses}
        ref={this.cardRef}
        data-has-header={displayHeaderRow}
        {..._dataAttributes}
        {...{ [domAttributes.isExpanded]: expanded }}
        {...{ [domAttributes.modelFieldPath]: _modelFieldPath }}
      >
        {popover}

        {displayHeaderRow && (
          <Row className={headerClasses} onClick={() => isCollapsible && this.toggle()}>
            {titleCol} {actionCol}
            {message ? (
              <Col xs={12}>
                <span className={cardProps?.errorFormat ? "card-error" : "card-warning"}>
                  {typeof message === "string" ? loc(message) : Array.isArray(message) ? loc(...message) : null}
                </span>
              </Col>
            ) : (
              ""
            )}
            {category && (
              <Col xs={12}>
                {/* A category can be a React element, which cannot be in a paragraph when it has blocks */}
                {typeof category === "string" ? <p className="category">{category}</p> : <div className="category">{category}</div>}
              </Col>
            )}
          </Row>
        )}

        {isCollapsible && (
          <PanelOuter expanded={expanded} onToggle={this.toggle} modelFieldPath={_modelFieldPath} cardTitle={title}>
            {body}
          </PanelOuter>
        )}

        {!isCollapsible && body}

        {stats !== undefined || legend !== undefined ? (
          <div className={"footer" + (ftTextCenter ? " text-center" : "")}>
            {legend !== undefined ? <div className="legend">{legend}</div> : null}
            {stats !== undefined ? <hr /> : null}
            {stats !== undefined ? <div className="stats">{stats}</div> : null}
          </div>
        ) : null}

        {showModalPopover && <EntityModelModal entity={debugInfo} close={() => this.setState({ showModalPopover: false })} />}
      </div>
    )

    if (linkTo) {
      if (download) {
        return (
          <a onClick={() => downloadFileFromUrl(linkTo, "Downloaded", "downloading")} className="text-color">
            {card}
          </a>
        )
      } else if (linkTo.startsWith("http")) {
        return (
          <a href={linkTo} className="text-color">
            {card}
          </a>
        )
      } else {
        return (
          <Link to={linkTo.startsWith("#") ? linkTo.substring(1) : linkTo} className="text-color">
            {card}
          </Link>
        )
      }
    }

    return card
  }
}

/**
 * Helper method to let the js framework know whether or to render again a card and prevent unecessary UI flickering.
 * The card index is not reliable because it might change.
 * WARNING : in layouts a card can be returned several times from the same object.
 * Therefore don't assign the key returned from this function to the card object otherwise the key of last instance of the card
 * will be set to all previous instances of it by mutation, leading to buggy rendering effects.
 * @param {[Object]} cards The array of cards in which belongs the card
 * @param {Object} card The card for which to determine a key
 * @param {Number} cardIndex The card index in the arrays of cards
 * @returns The card key
 */
function getCardKey(cards, card, cardIndex) {
  if (card.key) return card.key

  let nbOfSameCards = 0
  const cardName = card.card

  if (!cardName) {
    console.warn(`A card without a name has been passed at position ${cardIndex}`, card)
    return cardIndex
  }

  for (const _card of cards) {
    if (!_card) continue
    if (_card.card === cardName) nbOfSameCards++
    if (nbOfSameCards === 2) break
  }

  return nbOfSameCards > 1 ? cardName + cardIndex : cardName
}

function toggleRightCol() {
  const sideColumnAttr = "data-show-side-column"
  const sideColumnElement = document.querySelector(`[${sideColumnAttr}]`)
  const sideColumnAttrValue = sideColumnElement.getAttribute(sideColumnAttr)

  const newRightColAttrValue = sideColumnAttrValue === "true" ? "false" : sideColumnAttrValue === "false" ? "sticky" : "true"

  sideColumnElement.setAttribute(sideColumnAttr, newRightColAttrValue)
  setUiState(localStorageKeys.UI_STATE.CARD.SHOW_SIDE_COLUMN, newRightColAttrValue)
}

export function generatePageCards({
  layout,
  getWorkflowCard,
  getCardComponent,
  useAppLoadingAnimation,
  modal = {},
  showRightColToggleBtn = true,
  showCollapseToggleBtn,
} = {}) {
  const cards = layout?.cards || []
  const hasCards = cards.length > 0
  if (!hasCards) {
    return useAppLoadingAnimation ? <AppLoadingAnimation /> : <LazySkeletonUi noPadding />
  }

  const firstCard = cards[0]
  // The prop position: "static" of the workflow card opts out of the style option entityWorkflowCardPosition: "sticky"
  const isFirstCardWorkflowSticky =
    firstCard?.card === cardNames.WORKFLOW &&
    firstCard?.props?.hidden !== true &&
    firstCard?.props?.position !== "static" &&
    getConfigAtPath("styles.entityWorkflowCardPosition") === "sticky"

  const cardsColLeft = []
  const cardsColRight = []
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i]
    if (!card || card.props?.hidden) continue
    if (isFirstCardWorkflowSticky && i === 0) continue

    const cardKey = getCardKey(cards, card, i)
    const cardMap = { card, cardKey }
    if (card.props?.colRight === true) {
      cardsColRight.push(cardMap)
    } else {
      cardsColLeft.push(cardMap)
    }
  }

  const hasRightCol = cardsColRight.length > 0
  const getCardsItems = cardsArray => {
    let navsComponentCount = 0
    return cardsArray.map(({ card, cardKey }) => {
      if (typeof getCardComponent === "function") {
        let _card = card
        if (card.card === cardNames.NAVS) {
          _card = { ...card }
          _card.props = card.props ? { ...card.props } : {}
          _card.props.isFirstNavsCard = navsComponentCount === 0
          navsComponentCount++
        }

        const cardComponent = card.card === cardNames.WORKFLOW ? getWorkflowCard(_card) : getCardComponent({ card: _card, layout })
        const { colProps, navs } = card.props || {}
        const { xs = 12, sm, md, lg, className = "" } = colProps || {}

        let style = null
        if (card.card !== cardNames.NAVS) {
          const _navs = Array.isArray(navs) ? navs : navs?.split(",")
          let displayStyle = ""
          for (let i = 0; i < _navs?.length; i++) {
            const nav = _navs[i].trim()
            displayStyle = i === 0 ? `var(${navCss.varPrefix}${nav}, none)` : `var(${navCss.varPrefix}${nav}, ${displayStyle})`
          }
          if (displayStyle) {
            displayStyle = `var(${navCss.varPrefix}${navCss.varAllSuffix}, ${displayStyle})`
            style = { display: displayStyle }
          }
        }

        return (
          <Suspense key={cardKey} fallback={null}>
            {React.isValidElement(cardComponent) ? (
              <Col {...{ xs, sm, md, lg }} style={style} className={className}>
                {cardComponent}
              </Col>
            ) : null}
          </Suspense>
        )
      }
    })
  }

  let rightColButton
  if (hasRightCol && showRightColToggleBtn) {
    // Left of the collapse button when there is one, otherwise at its place on the right
    const dataOffsets = showCollapseToggleBtn ? 1 : 0
    rightColButton = (
      <div
        id={sideColumnToggleId}
        className="global-bottom-btn global-bottom-btn-side-col md_d-none"
        data-offsets={dataOffsets}
        onClick={toggleRightCol}
      >
        <ButtonWithTooltip
          className="icn-chevron-right icn-sm text-white"
          btnClassName="m-0"
          bsStyle="neutral"
          tooltip="Toggle or pin side column (CTRL + B)"
        />
      </div>
    )
  }

  let collapseToggleBtn
  if (showCollapseToggleBtn) {
    collapseToggleBtn = (
      <div
        id={cardsCollapseToggleId}
        className="global-bottom-btn"
        // same events as the ALT + C shortcut in App.js, so that inner panels toggle along with the cards
        onClick={() => {
          window.dispatchEvent(new CustomEvent(customEvents.card.toggle))
          window.dispatchEvent(new CustomEvent(customEvents.innerPanel.toggle))
        }}
        data-offsets="0"
      >
        <ButtonWithTooltip
          className="icn-arrows-to-line icn-sm text-white"
          btnClassName="m-0"
          bsStyle="neutral"
          tooltip="Collapse or expand all cards (ALT + C)"
        />
      </div>
    )
  }

  return (
    <>
      {isFirstCardWorkflowSticky && typeof getWorkflowCard === "function" && getWorkflowCard(firstCard)}

      <Row
        data-show-side-column={String(
          getUiState(localStorageKeys.UI_STATE.CARD.SHOW_SIDE_COLUMN) ?? getConfigAtPath("styles.defaultRightColPosition") ?? true,
        )}
      >
        <Col className="left-col" {...(layout?.colLeftColProps || (hasRightCol ? { xs: 12, md: 8 } : { xs: 12 }))}>
          <Row>{getCardsItems(cardsColLeft)}</Row>
        </Col>

        {/* These buttons must be next to each other in the DOM so that tab key navigation goes from to the other sequentially */}
        {rightColButton}
        {collapseToggleBtn}

        {hasRightCol && (
          <Col className="right-col" {...(layout?.colRightColProps || { xs: 12, md: 4 })}>
            <Row>{getCardsItems(cardsColRight)}</Row>
          </Col>
        )}
      </Row>

      {modal && (
        <Modal
          show={modal.show}
          onHide={modal.onHide}
          backdrop={modal.backdrop}
          data-page-name={modal.pageName}
          style={modal.style}
          bsSize={modal.size}
        >
          {modal.header && (
            <Modal.Header closeButton={modal.closeButton}>
              <Modal.Title>
                {typeof modal.header === "string"
                  ? loc(modal.header)
                  : modal.header?.cards
                    ? generatePageCards({ layout: modal.header, getCardComponent })
                    : null}
              </Modal.Title>
            </Modal.Header>
          )}

          {modal.body?.cards && <Modal.Body>{generatePageCards({ layout: modal.body, getCardComponent })}</Modal.Body>}

          {modal.footer?.cards && <Modal.Footer>{generatePageCards({ layout: modal.footer, getCardComponent })}</Modal.Footer>}
        </Modal>
      )}
    </>
  )
}

/**
 * `hideKpis` is a legacy option from when KPIs were displayed by default.
 * KPIs are now disabled by default. To display them, use the `showKpis` option instead.
 * This change ensures that KPI queries are explicitly enabled, helping prevent unintentional and potentially expensive database requests.
 */
export function getEntitiesPageKpisCard({ pageConfig, contentName }) {
  if (isOffline() || pageConfig?.hideKpis || (!getOptions("showKpisByDefaultInEntitiesPages") && !pageConfig?.showKpis)) {
    return null
  }

  return (
    <LayoutCard
      noCard
      rows={[
        {
          type: "content",
          props: {
            getContentOnUrlChange: false,
            name: contentName,
            noCard: false,
            acceptMissing: true,
          },
        },
      ]}
    />
  )
}

export default withRouter(Card)
